
import { MealComposition, MealCompositionWithRecipes, DbMealComposition } from "@/types/mealComposition";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/services/auth";
import { withErrorHandling } from "../utils/errorHandling";
import { 
  formatMealCompositionForDbInsert, 
  formatMealCompositionForDbUpdate, 
  mapDbMealCompositionToMealComposition, 
  populateCompositionWithRecipes 
} from "./mealCompositionUtils";
import { mealCompositionTagService } from "./mealCompositionTagService";
import { recipeService } from "@/services/recipe";

/**
 * Service for basic CRUD operations on meal compositions
 */
export const mealCompositionCrudService = {
  /**
   * Create a new meal composition
   */
  createMealComposition: withErrorHandling(
    async (composition: Omit<MealComposition, 'id' | 'createdAt' | 'updatedAt' | 'userId' | 'familyId' | 'tags'>): Promise<MealCompositionWithRecipes | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      
      const userId = currentUser.userId;
      
      // Fetch user's current family ID from their profile
      console.log('Fetching user profile for family ID...');
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("current_family_id")
        .eq("id", userId)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        throw new Error(`Failed to fetch user profile: ${profileError.message}`);
      }

      if (!profileData) {
        throw new Error("User profile not found");
      }

      const userFamilyId = profileData.current_family_id;
      
      if (!userFamilyId) {
        throw new Error("User is not associated with a family. Please join or create a family first.");
      }

      console.log('Using user family ID for composition:', userFamilyId);
      
      // Format composition for database insertion with empty tags initially
      const compositionWithTags = { ...composition, tags: [] };
      const formattedComposition = formatMealCompositionForDbInsert(compositionWithTags, userId, userFamilyId);
      
      console.log('Inserting composition with data:', formattedComposition);
      const { data, error: saveError } = await supabase
        .from("meal_compositions")
        .insert(formattedComposition)
        .select()
        .single();

      if (saveError) {
        console.error('Error saving composition:', saveError);
        throw saveError;
      }

      console.log('Composition saved successfully:', data);

      // Convert from database format
      const savedComposition = mapDbMealCompositionToMealComposition(data as DbMealComposition);

      // Fetch recipes to populate the composition
      const recipes = await recipeService.getRecipes();
      const populatedComposition = populateCompositionWithRecipes(savedComposition, recipes);

      // Generate tags after saving (similar to quick recipes)
      try {
        const recipeNames = populatedComposition.recipes.map(recipe => recipe.title);
        if (recipeNames.length > 0) {
          console.log('Generating tags for composition:', savedComposition.name);
          const generatedTags = await mealCompositionTagService.generateAndSaveCompositionTags(
            savedComposition.id,
            savedComposition.name,
            recipeNames
          );
          
          // Update the returned composition with the generated tags
          populatedComposition.tags = generatedTags;
          console.log('Tags generated and saved:', generatedTags);
        }
      } catch (tagError) {
        console.error('Failed to generate tags for composition:', tagError);
        // Don't throw error for tag generation failure, just log it
      }

      return populatedComposition;
    },
    "Error creating meal composition"
  ),

  /**
   * Update an existing meal composition
   */
  updateMealComposition: withErrorHandling(
    async (id: string, updates: Partial<MealComposition>): Promise<MealCompositionWithRecipes | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("No authenticated user found");
      }
      
      const userId = currentUser.userId;
      
      // Format updates for database
      const formattedUpdates = formatMealCompositionForDbUpdate(updates, userId);
      
      const { data, error: updateError } = await supabase
        .from("meal_compositions")
        .update(formattedUpdates)
        .eq("id", id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      // Convert from database format
      const updatedComposition = mapDbMealCompositionToMealComposition(data as DbMealComposition);

      // Fetch recipes to populate the composition
      const recipes = await recipeService.getRecipes();

      // Return populated composition
      return populateCompositionWithRecipes(updatedComposition, recipes);
    },
    "Error updating meal composition"
  ),

  /**
   * Delete a meal composition by ID
   * This will also remove the composition from any meal plans that reference it
   */
  deleteMealComposition: withErrorHandling(
    async (id: string): Promise<boolean> => {
      console.log('Starting deletion process for composition:', id);
      
      // First, delete all meal plans that reference this composition
      console.log('Deleting related meal plans...');
      const { error: mealPlanError } = await supabase
        .from("meal_plans")
        .delete()
        .eq("meal_composition_id", id);

      if (mealPlanError) {
        console.error('Error deleting related meal plans:', mealPlanError);
        throw new Error(`Failed to delete related meal plans: ${mealPlanError.message}`);
      }

      console.log('Related meal plans deleted successfully');

      // Then delete the composition itself
      console.log('Deleting composition...');
      const { error: compositionError } = await supabase
        .from("meal_compositions")
        .delete()
        .eq("id", id);

      if (compositionError) {
        console.error('Error deleting composition:', compositionError);
        throw new Error(`Failed to delete composition: ${compositionError.message}`);
      }

      console.log('Composition deleted successfully');
      return true;
    },
    "Error deleting meal composition"
  )
};
